import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  User,
  Box,
  Zap,
  Cloud,
  Eye,
  Sparkles,
  Check,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'

export interface AttributeCardData {
  id?: string
  key: string
  displayName: string
  category: string
  dataType: string
  description?: string
  sourceType?: string
  isMultiValue?: boolean
  enumValues?: string[]
  isProposed?: boolean
  rationale?: string
}

interface AttributeCardProps {
  data: AttributeCardData
  onApprove?: () => void
  onReject?: () => void
}

const CATEGORY_CONFIG: Record<string, {
  icon: typeof User
  accent: string
  iconBg: string
  iconText: string
  badge: string
}> = {
  subject: {
    icon: User,
    accent: 'border-l-blue-500 dark:border-l-blue-400',
    iconBg: 'bg-blue-100 dark:bg-blue-900/50',
    iconText: 'text-blue-600 dark:text-blue-400',
    badge: 'border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/50',
  },
  resource: {
    icon: Box,
    accent: 'border-l-emerald-500 dark:border-l-emerald-400',
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/50',
    iconText: 'text-emerald-600 dark:text-emerald-400',
    badge: 'border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/50',
  },
  action: {
    icon: Zap,
    accent: 'border-l-orange-500 dark:border-l-orange-400',
    iconBg: 'bg-orange-100 dark:bg-orange-900/50',
    iconText: 'text-orange-600 dark:text-orange-400',
    badge: 'border-orange-200 dark:border-orange-700 text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-950/50',
  },
  environment: {
    icon: Cloud,
    accent: 'border-l-violet-500 dark:border-l-violet-400',
    iconBg: 'bg-violet-100 dark:bg-violet-900/50',
    iconText: 'text-violet-600 dark:text-violet-400',
    badge: 'border-violet-200 dark:border-violet-700 text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-950/50',
  },
}

const DEFAULT_CONFIG = CATEGORY_CONFIG.subject

export function AttributeCard({ data, onApprove, onReject }: AttributeCardProps) {
  const category = data.category?.toLowerCase() ?? 'subject'
  const config = CATEGORY_CONFIG[category] ?? DEFAULT_CONFIG
  const Icon = config.icon

  return (
    <div
      className={cn(
        'my-2 rounded-lg border border-border dark:border-border/40 bg-card shadow-sm overflow-hidden border-l-[3px]',
        config.accent,
        data.isProposed && 'border-dashed border-l-solid'
      )}
    >
      <div className="flex items-center gap-3 px-3.5 py-2.5">
        <div className={cn('flex items-center justify-center size-6 rounded-md shrink-0', config.iconBg)}>
          {data.isProposed ? (
            <Sparkles className={cn('size-3', config.iconText)} strokeWidth={1.5} />
          ) : (
            <Icon className={cn('size-3', config.iconText)} strokeWidth={1.5} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-foreground font-mono truncate">{data.key}</span>
            <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 shrink-0 uppercase font-semibold', config.badge)}>
              {category}
            </Badge>
            <span className="text-[10px] text-muted-foreground font-mono">{data.dataType}</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-[11px] text-muted-foreground">
            {data.displayName && data.displayName !== data.key && (
              <span>{data.displayName}</span>
            )}
            {data.sourceType && <span>{data.sourceType}</span>}
            {data.isMultiValue && <span>Multi-value</span>}
          </div>
        </div>

        {/* Inline actions for compact layout */}
        {data.isProposed && (onApprove || onReject) && (
          <div className="flex items-center gap-1.5 shrink-0">
            {onApprove && (
              <Button size="sm" className="h-7 gap-1 text-xs px-2.5" onClick={onApprove}>
                <Check className="size-3" /> Approve
              </Button>
            )}
            {onReject && (
              <Button size="sm" variant="outline" className="h-7 gap-1 text-xs px-2.5" onClick={onReject}>
                <X className="size-3" /> Reject
              </Button>
            )}
          </div>
        )}
        {!data.isProposed && data.id && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 gap-1 text-xs px-2 text-muted-foreground shrink-0"
            onClick={() => window.open(`/pac/attributes?selected=${data.id}`, '_blank')}
          >
            <Eye className="size-3" /> View
          </Button>
        )}
      </div>

      {/* Extra details row if present */}
      {(data.enumValues?.length || data.description || data.rationale) && (
        <div className="px-3.5 pb-2.5 pt-0 space-y-1">
          {data.enumValues && data.enumValues.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {data.enumValues.map((val, i) => (
                <Badge
                  key={i}
                  variant="secondary"
                  className="text-[10px] font-normal bg-muted dark:bg-muted/30 border border-border/70 py-0"
                >
                  {val}
                </Badge>
              ))}
            </div>
          )}
          {data.description && (
            <p className="text-xs text-muted-foreground leading-relaxed">{data.description}</p>
          )}
          {data.rationale && (
            <p className="text-xs text-muted-foreground dark:text-muted-foreground/60 italic leading-relaxed">{data.rationale}</p>
          )}
        </div>
      )}
    </div>
  )
}
